import { Editor } from "tinymce";
import CloseIcon from "assets/icons/close.svg?react";
import Button from "components/Button";
import Filedossier from "components/Filedossier/Filedossier";
import ConverstationContext from "contexts/ConversationContext";
import ModalsManagerContext from "contexts/ModalsManager";
import useFiles, { FileExt } from "hooks/useFiles";
import * as openpgp from "openpgp";
import { useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import encryptContent from "utils/encryptString";
import { generateSingleBinaryFromMultiplePublicKeys } from "utils/generateSingleBinaryFromMultiplePublicKeys";
import { getErrorMessage } from "utils/getErrorMessage";
import { getKeys } from "utils/getKeys";
import { getUserId } from "utils/getUserId";
import { ENotificationType, notification } from "utils/notification";
import { readAsBase64 } from "utils/readAsBase64";
import { readFileFromBase64 } from "utils/readFileFromBase64";
import { uploadBlob } from "utils/uploadBlob";
import { handleCreate } from "utils/uploadFile";
import { CaseDossierWrapper } from "./CaseDossierWrapper";
import styles from "./styles.module.scss";
import { FC, IUploadedFile, Props } from "./typings";

import Input from "components/Input";
import { useOnClickOutside } from "hooks/useClickOutside";
import { useNavigate, useParams } from "react-router-dom";
import ModalLayout, { DockPosition } from "components/ModalLayout";
import RichEditor from "components/RichEditor";
import classNames from "classnames";
import usePDF from "hooks/usePDF";
import ShowPreviewModal from "components/ShowPreviewModal";
import Spinner from "components/Spinner";
import DossierAnimation from "components/DossierAnimation";
import { isAllowedEDeliveryAttachment } from "utils/isAllowedEDeliveryAttachment";
import { isAttachmentNameValid } from "utils/isAttachmentNameValid";
import { logger } from "utils/logger";
import casesService from "api/casesService";
import { AbilityContext } from "contexts/AbilityContext";
import { ITag } from "dto/ITag";
import SearchLabel from "components/SearchLabel";
import TagBadge from "components/TagBadge";
import helpdeskService from "api/helpdeskService";
import { useTranslation } from "react-i18next";

interface IErrorMessage {
  title: string;
  message: string;
  hint?: string;
}

export enum CreateOutgoingParcelMode {
  Normal = "normal",
  Reply = "reply",
  Forward = "forward",
}

const CreateHelpdeskIssueModal: FC<Props> = () => {
  const { t } = useTranslation();
  const ability = useContext(AbilityContext);
  useParams();
  const auth = useAuth();
  const userId = getUserId(auth.user!);
  const [isDragOver, setIsDragOver] = useState(false);

  const [isLoadingContent] = useState(false);

  const outSideRef = useRef(null);
  const editorRef = useRef<any>(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  const [errorMesssage, setErrorMessage] = useState<IErrorMessage>(null);

  const externalEDeliveryFilesSizeLimit = 500 * 1024 * 1024;
  const emailFilesSizeLimit = 50 * 1024 * 1024;

  const [tags, setTags] = useState<ITag[]>([]);

  const {
    files,
    setFiles,
    addNewFile,
    removeFile,
    size: filesSize,
  } = useFiles();
  const inputFile = useRef<HTMLInputElement | null>(null);

  const [isLoading, setIsLoading] = useState(false);

  const { modals, setModals } = useContext(ModalsManagerContext);
  casesService.useRedirect();

  const { data: adminKeys } = helpdeskService.useGetIssueEncryptionKeys();
  const { mutate: createIssue } = helpdeskService.useCreateIssue();

  const [, setShowDossier] = useState<boolean>(false);
  const [showFullDossier, setShowFullDossier] = useState<boolean>(false);
  const [, setShowLetter] = useState<boolean>(false);

  const isDisabled = () => {
    if (!title.length) {
      return true;
    }

    for (const f of files) {
      if (!validateAttachment(f)) return true;
    }

    return false;
  };

  useOnClickOutside(outSideRef, () => {
    if (showAddAttachmentsDropdown) {
      setShowAddAttachmentsDropdown(false);
    }
  });

  const handleCloseError = () => {
    setErrorMessage(null);
  };

  const validateAttachment = (file: File) => {
    const ext = file.name.split(".").pop();

    if (!isAllowedEDeliveryAttachment(ext)) {
      return false;
    }

    if (!isAttachmentNameValid(file.name)) {
      return false;
    }

    if (filesSize > externalEDeliveryFilesSizeLimit) return false;
    if (filesSize > emailFilesSizeLimit) return false;

    return true;
  };

  const getAttachmentErrorMessage = (file: File) => {
    const ext = file.name.split(".").pop();

    if (!isAllowedEDeliveryAttachment(ext)) {
      return t("errors.invalidAttachmentFormat");
    }

    if (!isAttachmentNameValid(file.name)) {
      return t("errors.attachmentNameTooLong");
    }

    if (filesSize > externalEDeliveryFilesSizeLimit)
      return t("errors.messageSizeLimitExceeded");

    if (filesSize > emailFilesSizeLimit)
      return t("errors.messageSizeLimitExceeded");

    return null;
  };

  useContext(ConverstationContext);

  // W zależności od tego czy wybrano sprawę
  // zwraca klucz publiczny użytkownika lub sprawy
  // którym zostanie zaszyfrowany klucz prywatny przesyłki
  const getUserOrCasePublicKey = async (): Promise<openpgp.key.Key> => {
    const myKeys = await getKeys(userId);

    const masterPublicKey = await readFileFromBase64(
      myKeys!.publicBase64Key,
      "application/other"
    );
    const armored = new TextDecoder().decode(masterPublicKey);

    const t = await openpgp.key.readArmored(armored);

    return t.keys[0];
  };

  const handleUpdateProgressFile = (
    allFiles: FileExt[],
    fileIndex: number,
    progress: number
  ) => {
    const newFiles = [...allFiles];

    const f = newFiles[fileIndex];
    if (f) {
      f.progress = progress;
      setFiles(newFiles);
    }
  };

  const navigate = useNavigate();

  const handleSend = async () => {
    setIsLoading(true);

    await getUserOrCasePublicKey();
    const myKeys = await getKeys(userId);

    // Klucz obiektu dla przesyłki
    const parcelKey = await generateSingleBinaryFromMultiplePublicKeys([
      ...adminKeys.data.Keys.map((entity) => entity.PublicKey ?? "").filter(
        (e) => !!e.length
      ),
      myKeys!.publicBase64Key,
    ]);

    const sendContent = cleanHTML(content);

    logger.log("Encrypting content");
    // Szyfrowanie treści
    const encryptedContent = await encryptContent(
      sendContent,
      parcelKey.cleanPublicKey
    );

    const normalUri: any = [];
    const normalBlobUri: any = [];
    let encryptedUri: any = [];
    let encryptedBlobUri: any = [];

    if (files.length) {
      logger.log("Creating encrypted files from disk");
      console.time("handleCreate");
      encryptedUri = await handleCreate(
        files,
        "",
        parcelKey.cleanPublicKey,
        true,
        true,
        null,
        null,
        (fileIndex: number, progress: number) => {
          handleUpdateProgressFile(files, fileIndex, progress);
        },
        false
      );
      console.timeEnd("handleCreate");
    }

    if (uploadedFiles.length) {
      logger.log("Creating unencrypted files from existing files");
      console.time("uploadBlob");
      encryptedBlobUri = await uploadBlob(
        uploadedFiles,
        "",
        parcelKey,
        true,
        true
      );
      console.timeEnd("uploadBlob");
    }

    const uris = [
      normalUri,
      normalBlobUri,
      encryptedUri,
      encryptedBlobUri,
    ].flat();

    logger.log("Making request to BE");

    createIssue(
      {
        Attachments: uris.length
          ? uris?.map((url, index) => ({
              Order: index,
              Uri: url!.uri as string,
            }))
          : [],

        Content: await readAsBase64(encryptedContent),
        PrivateKey: parcelKey.PrivateKey,
        PublicKey: parcelKey.PublicKey,
        Title: title,
      },
      {
        onSuccess: (response: any) => {
          setIsLoading(false);
          setModals({ ...modals, createHelpdesk: false });
          console.log(response);
          if (response?.data?.ChatId) {
            navigate(`/helpdesk/issues/${response.data.ChatId}`);
          }
        },
        onError(e: any) {
          setIsLoading(false);
          notification({
            title: t("helpdesk.issueSendFailed"),
            text: getErrorMessage(e.response.data),
            type: ENotificationType.ERROR,
          });
        },
      }
    );
  };

  const handleChooseFile = async (event: any) => {
    event.stopPropagation();
    event.preventDefault();

    for (const file of (event.target as any).files) {
      const ext = (file as File).name.split(".").pop();

      if (!isAllowedEDeliveryAttachment(ext)) {
        setErrorMessage({
          title: t("errors.invalidAttachmentFormat"),
          message: t("errors.fileInvalidFormat", { name: (file as File).name }),
          hint: t("errors.supportedFormats"),
        });
        return;
      }
    }

    for (const file of (event.target as any).files) {
      addNewFile(file);
    }
  };

  const handleClickToUpload = () => {
    if (!inputFile.current) return;
    inputFile.current.click();
  };

  const [uploadedFiles, setUploadedFiles] = useState<IUploadedFile[]>([]);
  const [showAddAttachmentsDropdown, setShowAddAttachmentsDropdown] =
    useState(false);

  const handleAddFiles = (files: IUploadedFile[]) => {
    setUploadedFiles([...uploadedFiles, files].flat());
    setShowDossier(false);
    setShowLetter(false);
  };

  const removeUploadedFile = (sindex: number) => {
    setUploadedFiles(uploadedFiles.filter((a, index) => index !== sindex));
  };
  useEffect(() => {
    const elements = localStorage.getItem("send_item");

    if (elements?.length) {
      // addFile(JSON.parse(elements));
      localStorage.removeItem("send_item");
    }
  }, []);

  const cleanHTML = (htmlString: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    // Remove <summary> elements
    const summaryElements = doc.querySelectorAll("summary");
    summaryElements.forEach((summary) => {
      summary.remove();
    });

    // Remove <details> elements but preserve children
    const detailsElements = doc.querySelectorAll("details");
    detailsElements.forEach((details) => {
      // Replace <details> with its children (preserving the order)
      while (details.firstChild) {
        details.parentNode.insertBefore(details.firstChild, details);
      }
      details.remove();
    });

    return doc.body.innerHTML;
  };

  const refWrapper = useRef(null);

  const handleRegisterEditor = (editor: Editor) => {
    editor.on("click", (e) => {
      if (e.target?.nodeName === "SUMMARY") {
        editor.execCommand("ToggleAccordion", false);
      }
    });

    // fetch(attachIcon)
    //   .then((response) => response.text())
    //   .then((text) => {
    //     editor.ui.registry.addIcon("attachments", text);
    //     editor.ui.registry.addMenuButton("attachments", {
    //       icon: "attachments",
    //       text: "Załącznik",
    //       fetch: (callback) => {
    //         const items = [
    //           {
    //             type: "menuitem",
    //             text: "Z dysku",
    //             onAction: () => handleClickToUpload(),
    //           },
    //           {
    //             type: "menuitem",
    //             text: "Z zakładki dossier",
    //             onAction: () => {
    //               setShowDossier(true);
    //             },
    //           },
    //         ];

    //         callback(items);
    //       },
    //     });
    //   })
    //   .catch((error) => {
    //     console.error("Error fetching the icon:", error);
    //   });
  };

  const inputsRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef(null);

  const handleCloseModal = () => {
    setModals({ ...modals, hide: true });
  };

  const { blob, addBlob, removeBlob } = usePDF();

  const handleShowPreview = (file: File | IUploadedFile) => {
    if (file instanceof File) {
      const newBlob = new Blob([file], { type: file.type });
      addBlob(newBlob);
      return;
    }

    addBlob(file.file, file.name);
  };


  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    if (!wrapperRef.current) return;

    if (!isAncestor(e.relatedTarget, wrapperRef.current)) {
      console.log("Drag leave");
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
    }
  };

  const isAncestor = (node, target) => {
    if (node === target) return false;
    while (node.parentNode) {
      if (node.parentNode === target) return true;
      node = node.parentNode;
    }
    return false;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Required to allow drop
    e.dataTransfer.dropEffect = "copy";
    setIsDragOver(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    console.log("Files dropped:", files);

    for (const file of files) {
      addNewFile(file);
    }
  };

  const addNewTag = (tag: ITag | null) => {
    const isTag = tags.find((etag) => etag.Name === tag!.Name);
    if (isTag) return;
    setTags([...tags, tag!]);
  };

  const handleRemoveTag = (tag: ITag) =>
    setTags(tags.filter((etag) => tag.Name !== etag.Name));

  return (
    <ModalLayout
      name="createIssue"
      dock={DockPosition.DISABLED}
      off={true}
      customX={8}
      customY={0}
      ignore
      refWrapper={refWrapper}
      isAnimation
      fullHeight={true}
      widthAsLeftArea={true}
      disableMove={true}
    >
      <>
        <div
          className={styles.wrapper}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          ref={wrapperRef}
        >
          <div className={styles.title}>
            Helpdesk
            <div className={styles.icons}>
              <CloseIcon
                className={styles.closeIcon}
                onClick={handleCloseModal}
              />
            </div>
          </div>
          <div className={styles.content}>
            <div className={styles.inputs}>
              <div ref={inputsRef} className={styles.inputsWrapper}>
                <div className={styles.inputRow}>
                  <div
                    className={`${styles.inputControl} ${styles.dropdownParent}`}
                    ref={outSideRef}
                  >
                    <Input
                      label={t("forms.title")}
                      value={title}
                      variant="filled"
                      onChange={(text) => setTitle(text)}
                      hideClearButton={true}
                      onRightClick={() => {
                        if (!ability.can("accessModule", "drive")) {
                          handleClickToUpload();
                          return;
                        }
                        setShowAddAttachmentsDropdown(
                          !showAddAttachmentsDropdown
                        );
                      }}
                      onRightText={t("documents.attachments")}
                    />

                    {showAddAttachmentsDropdown && (
                      <div ref={outSideRef} className={styles.dropdown}>
                        <div
                          onClick={() => {
                            handleClickToUpload();
                            setShowAddAttachmentsDropdown(false);
                          }}
                          className={styles.box}
                        >
                          {t("common.fromLocalDisk")}
                        </div>
                        <div
                          onClick={() => {
                            setShowAddAttachmentsDropdown(false);
                            setShowFullDossier(true);
                          }}
                          className={styles.box}
                        >
                          {t("common.fromNotaCloud")}
                        </div>
                        {/* <div
                          onClick={() => {
                            if (!c) {
                              notification({
                                text: "Wybierz teczkę żeby dodać pisma",
                                title: "Teczka",
                                type: ENotificationType.ERROR,
                              });
                              return;
                            }

                            setShowAddAttachmentsDropdown(false);
                            setShowLetter(true);
                          }}
                          className={styles.box}
                        >
                          Z zakładki pisma
                        </div> */}
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.tags}>
                  <div className={styles.cr}>
                    <SearchLabel
                      label={t("forms.reportType")}
                      type={1}
                      onChoose={addNewTag}
                    />
                  </div>

                  <div className={styles.tagList}>
                    {!!tags.length && (
                      <>
                        {tags.map((tag) => (
                          <TagBadge
                            useBigChip
                            key={tag.Id}
                            tag={tag}
                            onRemove={handleRemoveTag}
                          />
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className={classNames(styles.editor, "test")}>
              <RichEditor
                register={handleRegisterEditor}
                setContent={setContent}
                ref={editorRef}
                content={content}
              />
              {isLoadingContent && <Spinner></Spinner>}
            </div>
            <div className={styles.attachments}>
              <div className={styles.attachWrap}>
                <div className={styles.attachItems}>
                  {files.map((file, i) => (
                    <Filedossier
                      key={`files_${i}`}
                      index={i}
                      actualFile={i}
                      actualProgress={file.progress}
                      file={file}
                      onClick={() => handleShowPreview(file)}
                      handleRemove={removeFile}
                      hasError={!validateAttachment(file)}
                      errorMessage={getAttachmentErrorMessage(file)}
                    />
                  ))}
                  {uploadedFiles.map((file, i) => (
                    <Filedossier
                      key={`uploadedFiles_${i}`}
                      index={i}
                      file={file}
                      onClick={() => handleShowPreview(file)}
                      handleRemove={removeUploadedFile}
                    />
                  ))}
                </div>

                {/* <FileUploader
                  multiple={true}
                  handleChange={async (f: any) => {
                    // handleChooseFile(f);
                    for (const file of f) {
                      addNewFile(file);
                    }
                  }}
                  classes={styles.drop}
                  name="file"
                /> */}
              </div>
            </div>
            <div className={styles.footer}>
              <Button
                loading={isLoading || isLoadingContent}
                className={styles.btn2}
                disabled={isDisabled()}
                onClick={handleSend}
                // text="Wyślij"
                element={<>{t("common.send")}</>}
              />
            </div>
          </div>

          <div
            className={styles.dragOver}
            style={{ display: isDragOver ? "flex" : "none" }}
          >
            <div className={styles.inner}>
              <div>{t("common.dropFilesHere")}</div>
              <DossierAnimation className={styles.img} />
            </div>
          </div>

          {errorMesssage && (
            <div className={styles.error}>
              <div className={styles.icons}>
                <CloseIcon
                  className={styles.closeIcon}
                  onClick={handleCloseError}
                />
              </div>
              <div className={styles.inner}>
                <div className={styles.header}>{errorMesssage.title}</div>
                <div className={styles.message}>{errorMesssage.message}</div>
                <div className={styles.hint}>{errorMesssage.hint}</div>
              </div>
            </div>
          )}
        </div>
        <input
          type="file"
          id="file"
          ref={inputFile}
          multiple={true}
          style={{ display: "none" }}
          onChange={handleChooseFile}
        />

        {showFullDossier && (
          <CaseDossierWrapper
            handleAddFiles={handleAddFiles}
            c={null}
            toggle={() => setShowFullDossier(false)}
          />
        )}
        {blob && <ShowPreviewModal toggle={removeBlob} blob={blob} />}
      </>
    </ModalLayout>
  );
};

export default CreateHelpdeskIssueModal;
